import * as React from 'react';
import { DateInput } from './date-input';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  description?: string;
  /** Date fields only: show the calendar in the field instead of as a popup. */
  inlineCalendar?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, description, id, inlineCalendar, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || props.name || generatedId;
    const Component = type === 'date' || type === 'datetime-local' ? DateInput : 'input';

    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <Component
          type={type}
          id={inputId}
          {...(Component === DateInput && inlineCalendar ? { inlineCalendar: true } : {})}
          aria-label={props['aria-label'] ?? label}
          className={cn(
            'border-brand-border/70 flex h-10 w-full rounded-md border bg-card px-3 py-2 text-sm text-foreground shadow-sm ring-offset-bg transition-colors',
            'file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'placeholder:text-muted/70',
            'focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
            'read-only:bg-bg/60 read-only:text-muted read-only:shadow-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-danger focus-visible:ring-danger',
            className,
          )}
          ref={ref}
          {...props}
        />
        {description && !error && <p className="text-xs text-muted">{description}</p>}
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
